<template>
  <b-modal
    :active.sync="show"
    @cancel="$emit('input', false)"
    @close="$emit('input', false)"
    :width="640"
    scroll="keep"
  >
    <div class="box form-collaborators-c">
      <h3 class="title is-4">
        {{ form._id ? "Actualizar" : "Agregar Nuevo" }} Colaborador
      </h3>

      <b-field
        label="Nombre Completo"
        :type="nameError ? 'is-danger' : null"
        :message="nameError"
      >
        <b-input
          v-model="form.name"
          placeholder="Ingresa el nombre completo"
          @input="touched.name = true"
          @blur="touched.name = true"
        ></b-input>
      </b-field>

      <b-field
        label="Correo electronico"
        :type="emailError ? 'is-danger' : null"
        :message="emailError"
      >
        <b-input
          ref="emailInput"
          v-model="form.email"
          type="email"
          placeholder="email@empresa.com"
          @input="touched.email = true"
          @blur="touched.email = true"
        ></b-input>
      </b-field>

      <p v-if="emailSuggestion" class="help email-suggestion">
        ¿Quisiste decir
        <a class="email-suggestion__link" @click="applyEmailSuggestion">{{
          emailSuggestion
        }}</a
        >?
      </p>

      <b-field
        label="Documento"
        :type="identificationError ? 'is-danger' : null"
        :message="identificationError"
      >
        <b-input
          v-model="form.identification"
          placeholder="Cédula"
          @input="touched.identification = true"
          @blur="touched.identification = true"
        ></b-input>
      </b-field>

      <b-field
        label="Tipo de encuesta"
        style="margin-top: 0.5rem;; width: 100% !important;"
      >
        <b-radio-button
          v-model="form.typeSurvey"
          native-value="0"
          type="is-info is-light is-outlined"
          style="width: 100% !important;"
          :disabled="form.status && form.status !== 'created'"
        >
          <b-icon icon="account-outline"></b-icon>
          <span>Cultura hoy</span>
        </b-radio-button>

        <b-radio-button
          v-model="form.typeSurvey"
          native-value="1"
          type="is-success is-light is-outlined"
          style="width: 100% !important;"
          :disabled="form.status && form.status !== 'started'"
        >
          <b-icon icon="account-star-outline"></b-icon>
          <span>Cultura hoy y deseada</span>
        </b-radio-button>
      </b-field>

      <div v-if="dependencies.length">
        <div class="dependencies-list">
          <DynamicField
            v-for="dep in dependencies"
            :key="dep._id"
            v-model="form.dependencies[`${dep._id}`]"
            :label="dep.name"
            :type-field="dep.type"
            :options="convertOptions(dep.options)"
            :input-props="{ expanded: true }"
          />
        </div>
      </div>

      <div class="is-flex is-justify-content-space-between mt-5">
        <b-button
          class="mr-5"
          type="is-dark"
          expanded
          @click="handleSubmitProcess"
          :disabled="!isFormValid"
        >
          {{ form._id ? "Actualizar" : "Agregar" }} Colaborador
        </b-button>
        <b-button expanded @click="$emit('input', false)">
          Cancelar
        </b-button>
      </div>
    </div>
  </b-modal>
</template>
<script>
import { mapState } from "vuex";
import store from "../../store";
import { fetchDependencies } from "../../api";
import DynamicField from "./DynamicField.vue";

// Common providers used to detect typos like gamil.com / hormail.com and
// suggest the correct domain before the address is ever saved.
const POPULAR_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "yahoo.com",
  "yahoo.es",
  "hotmail.es",
  "outlook.es",
  "live.com",
  "icloud.com",
  "msn.com",
  "aol.com",
  "protonmail.com",
];

// Levenshtein edit distance between two strings (row-rolled, O(n) memory).
function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;

  const prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prevDiag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = prev[j];
      prev[j] = Math.min(
        prev[j] + 1,
        prev[j - 1] + 1,
        prevDiag + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      prevDiag = temp;
    }
  }
  return prev[n];
}

export default {
  components: {
    DynamicField,
  },
  data() {
    return {
      dependencies: [],
      show: this.value,
      // Form
      form: {
        _id: null,
        name: "",
        email: "",
        identification: "",
        typeSurvey: "0",
        dependencies: {},
      },
      touched: {
        name: false,
        email: false,
        identification: false,
      },
    };
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    editForm: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    ...mapState(["idtoken"]),
    currentProcess() {
      return store.state.selectedProcess;
    },
    // Validate Form
    nameError() {
      if (!this.touched.name) return null;
      if (!this.form.name) return "Campo obligatorio";
      return null;
    },
    emailError() {
      if (!this.touched.email) return null;

      if (!this.form.email) return null; // "Campo obligatorio";

      const input = this.$refs.emailInput.$refs.input || this.$refs.emailInput;
      if (input && !input.checkValidity()) {
        return "Correo electronico inválido";
      }

      return null;
    },
    identificationError() {
      if (!this.touched.identification) return null;
      if (!this.form.identification) return "Campo obligatorio";
      return null;
    },
    // Suggests a corrected address when the domain is a close typo of a known
    // provider (e.g. juan@gamil.com → juan@gmail.com). Returns null otherwise.
    emailSuggestion() {
      const raw = (this.form.email || "").trim();
      const at = raw.lastIndexOf("@");
      if (at < 1) return null;

      const local = raw.slice(0, at);
      const domain = raw.slice(at + 1).toLowerCase();
      if (domain.length < 3 || POPULAR_DOMAINS.includes(domain)) return null;

      let best = null;
      let bestDistance = Infinity;
      POPULAR_DOMAINS.forEach((candidate) => {
        const distance = levenshtein(domain, candidate);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = candidate;
        }
      });

      // Only nudge on a genuinely close miss (1–2 edits).
      if (best && bestDistance > 0 && bestDistance <= 2) {
        return `${local}@${best}`;
      }
      return null;
    },
    isFormValid() {
      const noErrors =
        !this.nameError && !this.emailError & !this.identificationError;
      const allFilled =
        this.form.name.trim() !== "" &&
        // this.form.email.trim() !== "" &&
        this.form.identification.trim() !== "" &&
        (this.form._id ? !this.isEqualObjects(this.editForm, this.form) : true);
      return noErrors && allFilled;
    },
  },
  created() {
    if (this.idtoken) {
      this.getDependecies();
    }
  },
  methods: {
    async getDependecies() {
      return fetchDependencies(this.currentProcess._id).then(
        (res) => (this.dependencies = res)
      );
    },
    // Form
    onlyNumbers(e) {
      const key = e.keyCode ? e.keyCode : e.which;
      if (key < 48 || key > 57) {
        e.preventDefault();
      }
    },
    clearForm() {
      this.form = {
        _id: null,
        name: "",
        email: "",
        identification: "",
        typeSurvey: "0",
        dependencies: {},
      };
      this.touched = {
        name: false,
        email: false,
        identification: false,
      };
    },
    isEqualObjects(obj1, obj2) {
      return (
        JSON.stringify(obj1, Object.keys(obj1).sort()) ===
        JSON.stringify(obj2, Object.keys(obj2).sort())
      );
    },
    convertOptions(options) {
      if (!options || !options.length) return null;
      return options.map((item) => ({ label: item, value: item }));
    },
    applyEmailSuggestion() {
      if (!this.emailSuggestion) return;
      this.form.email = this.emailSuggestion;
      this.touched.email = true;
    },
    // Actions
    handleSubmitProcess() {
      if (!this.isFormValid) return;

      const dependencies = Object.entries(this.form.dependencies)
        .filter(
          ([_, value]) => value !== null && value !== undefined && value !== ""
        )
        .map(([dependency, value]) => ({
          dependency,
          value,
        }));

      if (this.form._id) {
        return this.$emit("handleUpdateCollaborator", {
          ...this.form,
          dependencies,
          typeSurvey: parseInt(this.form.typeSurvey),
        });
      }

      this.$emit("handleAddCollaborator", {
        ...this.form,
        dependencies,
        typeSurvey: parseInt(this.form.typeSurvey),
        idProcess: this.currentProcess._id,
      });
    },
  },
  watch: {
    idtoken(nv) {
      if (nv) {
        this.getDependecies();
      }
    },
    editForm: {
      handler(newVal) {
        if (Object.keys(newVal).length) {
          const validDependencyIds = this.dependencies.map((d) =>
            String(d._id)
          );

          const dependencies = newVal.dependencies
            .filter((d) =>
              validDependencyIds.includes(
                String(d.dependency._id || d.dependency)
              )
            )
            .reduce((acc, d) => {
              acc[String(d.dependency._id || d.dependency)] = d.value;
              return acc;
            }, {});

          this.form = {
            ...newVal,
            dependencies,
            typeSurvey: newVal.typeSurvey.toString(),
          };
        }
      },
      deep: true,
    },
    value(newVal) {
      if (!newVal) this.clearForm();
      this.show = newVal;
    },
    show(newVal) {
      this.$emit("input", newVal);
    },
  },
};
</script>
<style lang="scss" scoped>
.dependencies-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.email-suggestion {
  margin-top: -0.5rem;
  color: #7a7a7a;

  .email-suggestion__link {
    color: #7957d5;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/components/Collaborator/CreateCollaboratorModal.vue